import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { formatDate, cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartConfig, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { 
  BarChart as BarChartIcon, 
  Users, 
  TrendingUp, 
  TrendingDown,
  Loader2,
  Building,
  Filter
} from 'lucide-react';

interface AUData {
  tenant_id: string;
  customer_id: string;
  granularity: 'day' | 'week' | 'month';
  period_label: string;
  period_start: string;
  period_end: string;
  au: number;
  prev_au: number | null;
  period_abs_change: number | null;
  period_pct_change: string | null;
}

interface CustomerData {
  id: string;
  name: string;
}

interface AUWithCustomer extends AUData {
  customer_name?: string;
}

type MetricType = 'day' | 'week' | 'month';

interface MetricOption {
  value: MetricType;
  label: string;
  description: string;
}

const MAUAnalytics = () => {
  const [auData, setAuData] = useState<AUWithCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Record<string, string>>({});
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('month');

  const metricOptions: MetricOption[] = [
    { value: 'day', label: 'DAU', description: 'Usuários Ativos Diários' },
    { value: 'week', label: 'WAU', description: 'Usuários Ativos Semanais' },
    { value: 'month', label: 'MAU', description: 'Usuários Ativos Mensais' }
  ];

  useEffect(() => {
    fetchAUData();
  }, [selectedMetric]);

  const fetchAUData = async () => {
    try {
      setLoading(true);
      
      // Fetch AU data with customer information filtering by selected metric
      const { data: auDataResult, error: auError } = await supabase
        .from('analytics_au_by_tenant')
        .select(`
          *,
          customers(id, name)
        `)
        .eq('granularity', selectedMetric)
        .order('period_label', { ascending: false });

      if (auError) {
        console.error('Erro ao buscar dados de AU:', auError);
        throw auError;
      }

      // Create customer mapping and enrich data
      const customerMapping: Record<string, string> = {};
      const enrichedData = auDataResult?.map(item => {
        let customerName = 'Cliente não encontrado';
        
        if (item.customers) {
          if (Array.isArray(item.customers) && item.customers.length > 0) {
            customerName = item.customers[0].name;
            customerMapping[item.customer_id] = customerName;
          } else if (!Array.isArray(item.customers)) {
            customerName = (item.customers as any).name;
            customerMapping[item.customer_id] = customerName;
          }
        }
        
        return {
          ...item,
          customer_name: customerName
        };
      }) || [];
      
      setCustomers(customerMapping);
      setAuData(enrichedData);
    } catch (error) {
      console.error('Erro ao buscar dados de AU:', error);
      toast.error('Não foi possível carregar os dados de usuários ativos.');
      setAuData([]);
    } finally {
      setLoading(false);
    }
  };

  // Aggregate data for chart (sum AUs by period across all tenants)
  const aggregatedData = auData.reduce((acc, item) => {
    const existing = acc.find(a => a.period_label === item.period_label);
    if (existing) {
      existing.total_aus += item.au;
    } else {
      acc.push({
        period_label: item.period_label,
        total_aus: item.au
      });
    }
    return acc;
  }, [] as { period_label: string; total_aus: number }[])
  .sort((a, b) => a.period_label.localeCompare(b.period_label));

  // Calculate summary metrics
  const totalAUs = auData.reduce((sum, item) => sum + item.au, 0);
  const uniqueCustomers = new Set(Object.values(customers)).size;
  const latestPeriod = auData.length > 0 ? auData[0].period_label : '';
  
  // Calculate trend metrics
  const dataWithChanges = auData.filter(item => item.period_abs_change !== null);
  const positiveChanges = dataWithChanges.filter(item => (item.period_abs_change || 0) > 0);
  const negativeChanges = dataWithChanges.filter(item => (item.period_abs_change || 0) < 0);
  const avgAbsChange = dataWithChanges.length > 0 
    ? dataWithChanges.reduce((sum, item) => sum + Math.abs(item.period_abs_change || 0), 0) / dataWithChanges.length 
    : 0;
  const avgPctChange = dataWithChanges.length > 0
    ? dataWithChanges.reduce((sum, item) => sum + parseFloat(item.period_pct_change || '0'), 0) / dataWithChanges.length
    : 0;
  
  // Get current metric info
  const currentMetric = metricOptions.find(m => m.value === selectedMetric) || metricOptions[2];

  // Chart configuration
  const chartConfig = {
    total_aus: {
      label: `${currentMetric.label} Totais`,
      color: "#3B65F5",
    },
  } satisfies ChartConfig;

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col items-center text-center text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <BarChartIcon className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold mb-2">
                  Analytics {currentMetric.label}
                </h1>
                <p className="text-white/80 text-lg">
                  {currentMetric.description} por Cliente
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total AUs Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Total {currentMetric.label}</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    totalAUs.toLocaleString()
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Último período: {latestPeriod}
              </div>
            </CardContent>
          </Card>

          {/* Unique Customers Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <Building className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Clientes Únicos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    uniqueCustomers
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Com dados de {currentMetric.label}
              </div>
            </CardContent>
          </Card>

          {/* Average AU Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 rounded-md">
                    <TrendingUp className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">{currentMetric.label} Médio</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    Math.round(totalAUs / (uniqueCustomers || 1)).toLocaleString()
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Por cliente
              </div>
            </CardContent>
          </Card>

          {/* Trend Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    {avgPctChange >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-purple-600" />
                    )}
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Tendência Média</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    <span className={cn(
                      avgPctChange >= 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {avgPctChange >= 0 ? '+' : ''}{avgPctChange.toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Variação período a período
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insights Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Growth Insights */}
          <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <TrendingUp className="h-5 w-5" />
                Crescimentos Positivos
              </CardTitle>
              <CardDescription>
                Períodos com aumento de {currentMetric.label}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total de crescimentos:</span>
                    <span className="font-bold text-green-600">{positiveChanges.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Maior crescimento:</span>
                    <span className="font-bold text-green-600">
                      {positiveChanges.length > 0 
                        ? `+${Math.max(...positiveChanges.map(p => parseFloat(p.period_pct_change || '0'))).toFixed(1)}%`
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Taxa de crescimento:</span>
                    <span className="font-bold text-green-600">
                      {dataWithChanges.length > 0 
                        ? `${((positiveChanges.length / dataWithChanges.length) * 100).toFixed(1)}%`
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Decline Insights */}
          <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <TrendingDown className="h-5 w-5" />
                Declínios Negativos
              </CardTitle>
              <CardDescription>
                Períodos com redução de {currentMetric.label}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total de declínios:</span>
                    <span className="font-bold text-red-600">{negativeChanges.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Maior declínio:</span>
                    <span className="font-bold text-red-600">
                      {negativeChanges.length > 0 
                        ? `${Math.min(...negativeChanges.map(p => parseFloat(p.period_pct_change || '0'))).toFixed(1)}%`
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Taxa de declínio:</span>
                    <span className="font-bold text-red-600">
                      {dataWithChanges.length > 0 
                        ? `${((negativeChanges.length / dataWithChanges.length) * 100).toFixed(1)}%`
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
          <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
            <div className="grid flex-1 gap-1">
              <CardTitle className="flex items-center gap-2">
                <BarChartIcon className="h-5 w-5 text-blue-600" />
                Evolução Total de {currentMetric.label}
              </CardTitle>
              <CardDescription>
                Soma de {currentMetric.description.toLowerCase()} de todos os clientes ao longo do tempo
              </CardDescription>
            </div>
            <Select value={selectedMetric} onValueChange={(value: MetricType) => setSelectedMetric(value)}>
              <SelectTrigger className="w-[220px] rounded-lg sm:ml-auto" aria-label="Selecionar métrica">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {metricOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="rounded-lg">
                    <div className="flex flex-col">
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : aggregatedData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <div className="text-center">
                  <BarChartIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum dado de {currentMetric.label} disponível</p>
                </div>
              </div>
            ) : (
              <ChartContainer
                config={chartConfig}
                className="aspect-auto h-[300px] w-full"
              >
                <AreaChart data={aggregatedData}>
                  <defs>
                    <linearGradient id="fillTotalAus" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="var(--color-total_aus)"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="var(--color-total_aus)"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} />
                  <XAxis 
                    dataKey="period_label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={(value) => {
                      // Format based on granularity
                      if (selectedMetric === 'day') {
                        return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                      } else if (selectedMetric === 'week') {
                        return value.replace('ISO-Wk of ', '').substring(0, 10);
                      } else {
                        const [year, month] = value.split('-');
                        return `${month}/${year}`;
                      }
                    }}
                  />
                  <YAxis 
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <ChartTooltip 
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(value) => {
                          if (selectedMetric === 'day') {
                            return new Date(value).toLocaleDateString('pt-BR');
                          } else if (selectedMetric === 'week') {
                            return value;
                          } else {
                            const [year, month] = value.split('-');
                            return `${month}/${year}`;
                          }
                        }}
                        indicator="dot"
                      />
                    }
                  />
                  <Area
                    dataKey="total_aus"
                    type="natural"
                    fill="url(#fillTotalAus)"
                    stroke="var(--color-total_aus)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Detailed Table */}
        <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Dados Detalhados por Cliente e Período</CardTitle>
            <CardDescription>
              Tabela completa com dados de {currentMetric.label} e informações do cliente
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : auData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado de {currentMetric.label} disponível.
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>{currentMetric.label}</TableHead>
                      <TableHead>{currentMetric.label} Anterior</TableHead>
                      <TableHead>Variação Absoluta</TableHead>
                      <TableHead>Variação (%)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auData.map((item) => (
                      <TableRow key={`${item.tenant_id}-${item.period_label}`}>
                        <TableCell className="font-extrabold">
                          {item.customer_name || 'Cliente não encontrado'}
                        </TableCell>
                        <TableCell className="font-extrabold">
                          {(() => {
                            if (selectedMetric === 'day') {
                              return new Date(item.period_label).toLocaleDateString('pt-BR');
                            } else if (selectedMetric === 'week') {
                              return item.period_label;
                            } else {
                              const [year, month] = item.period_label.split('-');
                              return `${month}/${year}`;
                            }
                          })()}
                        </TableCell>
                        <TableCell className="font-extrabold text-blue-600">
                          {item.au.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {item.prev_au ? item.prev_au.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>
                          {item.period_abs_change !== null ? (
                            <span className={cn(
                              "font-extrabold",
                              item.period_abs_change > 0 ? "text-green-600" : 
                              item.period_abs_change < 0 ? "text-red-600" : "text-gray-600"
                            )}>
                              {item.period_abs_change > 0 ? '+' : ''}{item.period_abs_change.toLocaleString()}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {item.period_pct_change !== null ? (
                            <Badge 
                              variant={
                                parseFloat(item.period_pct_change) > 0 ? 'default' : 
                                parseFloat(item.period_pct_change) < 0 ? 'destructive' : 'secondary'
                              }
                              className="font-extrabold"
                            >
                              {parseFloat(item.period_pct_change) > 0 ? '+' : ''}{item.period_pct_change}%
                            </Badge>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Spacing */}
      <div className="h-16" />
    </div>
  );
};

export default MAUAnalytics;
